import { useEffect, useState } from 'react';
import { api } from '../utils/api.js';
import { readSession, writeSession, removeSession, KEYS } from '../utils/storage.js';
import { friendlyError } from '../utils/errors.js';
import DocumentManager from '../components/DocumentManager.jsx';

export default function Admin() {
  const [pin, setPin] = useState(readSession(KEYS.adminPin) || '');
  // We only consider the page authed once the PIN has been verified against
  // the worker. A stale/bad PIN in sessionStorage no longer auto-unlocks.
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState(null);
  // True while unlock() is waiting on checkAdminPin, so the Unlock button can
  // show it's working instead of appearing to do nothing on a slow connection.
  const [checkingPin, setCheckingPin] = useState(false);

  // On mount, if a PIN is already in sessionStorage validate it before
  // flipping `authed` true, so a stale/bad PIN can't auto-unlock the page.
  useEffect(() => {
    const cached = readSession(KEYS.adminPin);
    if (!cached) return;
    (async () => {
      const ok = await api.checkAdminPin();
      if (ok) setAuthed(true);
      else removeSession(KEYS.adminPin);
    })();
  }, []);

  async function unlock(e) {
    e.preventDefault();
    setError(null);
    setCheckingPin(true);
    writeSession(KEYS.adminPin, pin);
    try {
      const ok = await api.checkAdminPin();
      if (ok) {
        setAuthed(true);
      } else {
        removeSession(KEYS.adminPin);
        setError('Wrong PIN');
      }
    } catch (e) {
      // 429 here is the worker's wrong-PIN lockout, not a busy server.
      setError(e.status === 429
        ? 'Too many wrong PINs. Wait 15 minutes and try again.'
        : friendlyError(e, 'check that PIN'));
    } finally {
      setCheckingPin(false);
    }
  }

  function lock() {
    removeSession(KEYS.adminPin);
    setAuthed(false);
  }

  if (!authed) {
    return (
      <div className="px-5 py-6 max-w-md mx-auto">
        <form onSubmit={unlock} className="card">
          <h1 className="h-display text-xl text-white mb-1">Admin Access</h1>
          <p className="text-ink-400 text-sm mb-4">Enter the admin PIN.</p>
          <input
            type="password"
            inputMode="numeric"
            placeholder="PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="w-full px-4 py-3 mb-4 bg-ink-800 border border-ink-700 rounded-lg text-white text-center text-xl font-mono tracking-widest focus:outline-none focus:border-mra-red"
          />
          <button type="submit" disabled={checkingPin} className="btn-primary w-full">
            {checkingPin ? 'Checking…' : 'Unlock'}
          </button>
          {error && <p className="text-fail-400 text-sm mt-3 text-center">{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="px-5 py-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="h-display text-2xl text-white">Admin</h1>
          <p className="text-ink-400 text-sm">Tech Wiki management</p>
        </div>
        <button onClick={lock} className="btn-ghost">Lock</button>
      </div>

      {/* Tech Wiki / SOP — sync the Notion wiki + review unanswered questions */}
      <WikiAdminPanel />

      {/* Documents — add a tech sheet / wiring diagram for a model (upload or link) */}
      <CoveragePanel />
        <DocumentsAdminPanel />
    </div>
  );
}

// Add tech sheets / wiring diagrams for a model without scanning a tag first.
// Enter a model number, then use the shared DocumentManager (with link-add
// enabled) to upload files or paste links. Docs are keyed by model with the
// app's family-prefix matching, so they serve every full model in the family.
// What to fetch next. Coverage — a manual on file for the model — is the
// biggest lever on diagnosis quality, and this is the list the office works
// from: models scanned with nothing on file, the most-scanned families, how
// many sessions ran without a manual, and PDFs still lacking page images.
function CoveragePanel() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api.getCoverage(days)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(friendlyError(e, 'load the coverage report')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);
  const s = data?.sessions || {};
  const pdfsNeeding = (data?.pdfs || []).filter((p) => p.needs_images);
  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="h-display text-xl text-white">Coverage — what to fetch next</h2>
        <div className="flex gap-2">
          {[30, 90, 365].map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)} className={['px-3 py-2 min-h-[40px] rounded-lg text-sm border', days === d ? 'bg-mra-red/15 border-mra-red text-mra-red' : 'bg-ink-800 border-ink-700 text-ink-200'].join(' ')}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {err && <p className="text-fail-400 text-sm">⚠ {err}</p>}
      {loading && !data && <p className="text-ink-400 text-sm">Loading…</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <Stat label="Sessions" value={s.total ?? 0} />
            <Stat label="With manual" value={s.manual ?? 0} tone="pass" />
            <Stat label="No document" value={s.none ?? 0} tone="fail" />
            <Stat label="Verdicts ✓ / ✗" value={`${s.confirmed ?? 0} / ${s.wrong ?? 0}`} />
          </div>

          <div>
            <h3 className="text-ink-300 text-xs uppercase tracking-wider mb-2">Scanned with nothing on file · {data.models_without_docs.length}</h3>
            {data.models_without_docs.length === 0 ? <p className="text-ink-400 text-sm">Every scanned model has a document. Nice.</p> : (
              <ul className="divide-y divide-ink-800">
                {data.models_without_docs.map((m) => (
                  <li key={m.model_number} className="py-2 flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="text-white font-mono truncate">{m.model_number}</div>
                      <div className="text-ink-400 text-xs">{m.brand || '—'} · {m.appliance_type || '—'} · last {formatWhen(m.last_scan)}</div>
                    </div>
                    <span className="badge-source shrink-0">{m.scans} scan{m.scans === 1 ? '' : 's'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-ink-300 text-xs uppercase tracking-wider mb-2">Most-scanned families</h3>
            <ul className="divide-y divide-ink-800">
              {data.families.slice(0, 12).map((f) => (
                <li key={f.family} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <span className="text-white font-mono">{f.family}…</span>
                    <span className="text-ink-400 text-xs ml-2">{f.brand || '—'} · {f.appliance_type || '—'}</span>
                  </div>
                  <span className={Number(f.scans_with_docs) < Number(f.scans) ? 'badge-marginal shrink-0' : 'badge-pass shrink-0'}>
                    {f.scans_with_docs}/{f.scans} covered
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-ink-300 text-xs uppercase tracking-wider mb-2">PDFs without page images · {pdfsNeeding.length}</h3>
            <p className="text-ink-400 text-xs mb-2">Open the model's Result page and tap "Index page images for the AI" under the document. Scanned PDFs get OCR'd; wiring pages become readable to the AI.</p>
            <ul className="divide-y divide-ink-800">
              {pdfsNeeding.slice(0, 15).map((p) => (
                <li key={p.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <div className="text-white truncate">{p.file_name || `doc #${p.id}`}</div>
                    <div className="text-ink-400 text-xs font-mono">{p.model_number} · {p.doc_type} · {p.pages} page{p.pages === 1 ? '' : 's'} indexed</div>
                  </div>
                  <span className="badge-source shrink-0">{p.pages_with_images}/{p.pages || '?'} imaged</span>
                </li>
              ))}
            </ul>
          </div>

          {data.training_sets?.length > 0 && (
            <p className="text-ink-400 text-xs">Training knowledge: {data.training_sets.map((t) => `${t.source_set} ${t.n}`).join(' · ')}</p>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'pass' ? 'text-pass' : tone === 'fail' ? 'text-fail-400' : 'text-white';
  return (
    <div className="bg-ink-800 border border-ink-700 rounded-lg py-3">
      <div className={`text-2xl font-bold ${cls}`}>{value}</div>
      <div className="text-ink-400 text-xs">{label}</div>
    </div>
  );
}

function DocumentsAdminPanel() {
  const [model, setModel] = useState('');
  const [activeModel, setActiveModel] = useState('');

  function load(e) {
    e.preventDefault();
    const m = model.trim().toUpperCase();
    if (m) setActiveModel(m);
  }

  return (
    <div className="space-y-4 pt-4 border-t border-ink-700">
      <div>
        <h2 className="h-display text-xl text-white">Documents</h2>
        <p className="text-ink-400 text-sm">
          Pre-load a tech sheet or wiring diagram for a model — upload a file or paste a link.
        </p>
      </div>

      <form onSubmit={load} className="flex gap-2">
        <input
          type="text"
          placeholder="Model number (e.g. RF29BB)"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="flex-1 min-w-0 bg-ink-800 border border-ink-700 rounded-lg text-white px-3 py-2 font-mono focus:outline-none focus:border-mra-red"
        />
        <button type="submit" disabled={!model.trim()} className="btn-secondary shrink-0">
          Load
        </button>
      </form>

      {activeModel && (
        <div className="card">
          <DocumentManager modelNumber={activeModel} allowLink />
        </div>
      )}
    </div>
  );
}

// Self-contained Tech Wiki admin section (migration 0008). Manages its own
// loading/error state so it never clobbers the brand editor above. Rendered
// only inside the authed view, so every call already carries a valid PIN.
function WikiAdminPanel() {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { pages, chunks }
  const [gaps, setGaps] = useState([]);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const [err, setErr] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  async function loadGaps() {
    setLoadingGaps(true);
    setErr(null);
    try {
      const data = await api.listWikiGaps();
      setGaps(data.gaps || []);
    } catch (e) {
      setErr(friendlyError(e, 'load the wiki gap list'));
    } finally {
      setLoadingGaps(false);
    }
  }

  useEffect(() => { loadGaps(); }, []);

  async function sync() {
    setSyncing(true);
    setErr(null);
    setSyncResult(null);
    try {
      const data = await api.syncWiki();
      setSyncResult(data);
    } catch (e) {
      // A "Notion not configured" 400 is expected until Jason connects Notion —
      // surface its message plainly rather than a generic failure.
      setErr(e?.data?.error || friendlyError(e, 'sync the wiki'));
    } finally {
      setSyncing(false);
    }
  }

  async function resolve(id) {
    setResolvingId(id);
    setErr(null);
    try {
      await api.resolveWikiGap(id);
      setGaps((prev) => prev.filter((g) => g.id !== id));
    } catch (e) {
      setErr(friendlyError(e, 'resolve that gap'));
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <div className="space-y-4 pt-4 border-t border-ink-700">
      <div>
        <h2 className="h-display text-xl text-white">Tech Wiki / SOP</h2>
        <p className="text-ink-400 text-sm">Sync the Notion wiki and review questions it couldn't answer.</p>
      </div>

      {err && (
        <div className="card border-fail/30 bg-fail/5">
          <p className="text-fail-400 text-sm">{err}</p>
        </div>
      )}

      <div className="card space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-white font-semibold">Wiki content</div>
            <div className="text-ink-400 text-sm">
              Pull the latest from Notion into the assistant's index.
            </div>
          </div>
          <button onClick={sync} disabled={syncing} className="btn-primary shrink-0">
            {syncing ? 'Syncing…' : 'Sync wiki'}
          </button>
        </div>
        {syncResult && (
          <div className="text-sm border-t border-ink-700 pt-3 space-y-1">
            <div className="text-pass">
              ✓ Synced {syncResult.pages} page{syncResult.pages === 1 ? '' : 's'} · {syncResult.chunks} chunk{syncResult.chunks === 1 ? '' : 's'} indexed.
            </div>
            {syncResult.failed?.length > 0 && (
              <div className="text-marginal">
                ⚠ {syncResult.failed.length} page{syncResult.failed.length === 1 ? '' : 's'} failed and kept
                {syncResult.failed.length === 1 ? ' its' : ' their'} previous content — sync again to retry.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-semibold">
            Unanswered questions
            {gaps.length > 0 && <span className="text-ink-400 font-normal"> ({gaps.length})</span>}
          </div>
          <button onClick={loadGaps} disabled={loadingGaps} className="btn-ghost text-xs">
            {loadingGaps ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {!loadingGaps && gaps.length === 0 && (
          <p className="text-ink-400 text-sm">No open gaps — the wiki is covering what techs are asking. 🎉</p>
        )}

        <div className="space-y-2">
          {gaps.map((g) => (
            <div key={g.id} className="flex items-start justify-between gap-3 bg-ink-800 border border-ink-700 rounded-lg p-3">
              <div className="min-w-0 flex-1">
                <div className="text-ink-100 text-sm break-words">{g.question}</div>
                <div className="text-ink-400 text-xs mt-1">
                  {g.repeat_count > 1 && <span className="text-marginal font-semibold">Asked {g.repeat_count}× · </span>}
                  {g.tech_name ? `${g.tech_name} · ` : ''}{formatWhen(g.last_asked)}
                </div>
              </div>
              <button
                onClick={() => resolve(g.id)}
                disabled={resolvingId === g.id}
                className="btn-ghost text-xs shrink-0"
                title="Mark this handled (e.g. after adding it to the wiki)"
              >
                {resolvingId === g.id ? '…' : 'Resolve'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// SQLite CURRENT_TIMESTAMP is "YYYY-MM-DD HH:MM:SS" in UTC. Render a short
// local date; fall back to the raw string if parsing fails.
function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
